import type { components } from '@leanin/api-client/schema';

export type MemberSearchHit = components['schemas']['MemberSearchHit'];
export type CircleSearchHit = components['schemas']['CircleSearchHit'];
export type NetworkSearchHit = components['schemas']['NetworkSearchHit'];
export type ResourceSearchHit = components['schemas']['ResourceGlobalSearchHit'];
export type EventSearchHit = components['schemas']['EventGlobalSearchHit'];
export type PostSearchHit = components['schemas']['PostGlobalSearchHit'];
export type MessageSearchHit = components['schemas']['MessageGlobalSearchHit'];

// SpaceSearchHit is inlined here because the generated schema's
// 'SpaceSearchHit' name is shared with the spaces feature's
// SpaceSummaryWithViewerRole intersection, producing a wider type than what
// the search endpoints actually return. The shape below is a direct
// translation of SpaceHitSchema in apps/server/src/search/routes/hit-schemas.ts.
export type SpaceSearchHit = {
  readonly kind: 'space';
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly access: 'public' | 'unlisted' | 'private';
  readonly memberCount: number;
};

export type CommentSearchHit = components['schemas']['CommentSearchHit'];

// ---- Suggest / results surfaces (search-platform §3.4) ----

export const SEARCH_TABS = [
  'members',
  'groups',
  'events',
  'resources',
  'posts',
  'messages',
] as const;
export type SearchTab = (typeof SEARCH_TABS)[number];
export type SearchResultsType = 'all' | SearchTab;

// The logged-out /search surface spans only the public directories
// (GET /search/public-results): Circles + Networks under `groups`, and
// Resources.
export const PUBLIC_SEARCH_RESULTS_TYPES = ['all', 'groups', 'resources'] as const;
export type PublicSearchResultsType = (typeof PUBLIC_SEARCH_RESULTS_TYPES)[number];

export function isPublicSearchResultsType(type: SearchResultsType): type is PublicSearchResultsType {
  return (PUBLIC_SEARCH_RESULTS_TYPES as readonly SearchResultsType[]).includes(type);
}

/** Which results surface a /search request targets: members search
 *  everything they can see, logged-out visitors the public directories. */
export type SearchResultsScope =
  | { readonly kind: 'member'; readonly type: SearchResultsType }
  | { readonly kind: 'public'; readonly type: PublicSearchResultsType };

// Mirrors the generated SearchResultItem but with the narrow SpaceSearchHit
// (same generated-name collision as above). Fetchers cast through unknown at
// the boundary; the runtime payload matches this shape.
export type SearchResultItem = {
  readonly score: number;
  readonly highlight: string | null;
} & (
  | { readonly kind: 'member'; readonly member: MemberSearchHit }
  | { readonly kind: 'circle'; readonly circle: CircleSearchHit }
  | { readonly kind: 'network'; readonly network: NetworkSearchHit }
  | { readonly kind: 'space'; readonly space: SpaceSearchHit }
  | { readonly kind: 'event'; readonly event: EventSearchHit }
  | { readonly kind: 'resource'; readonly resource: ResourceSearchHit }
  | { readonly kind: 'post'; readonly post: PostSearchHit }
  | { readonly kind: 'comment'; readonly comment: CommentSearchHit }
  | { readonly kind: 'message'; readonly message: MessageSearchHit }
);

export type SearchSuggestSection = {
  readonly tab: SearchTab;
  readonly items: SearchResultItem[];
};

export type SearchSuggestPage = {
  readonly queryEventId: string | null;
  readonly sections: SearchSuggestSection[];
};

export type SearchResultsPage = {
  readonly queryEventId: string | null;
  readonly mode: 'hybrid' | 'lexical';
  readonly items: SearchResultItem[];
  readonly nextCursor: string | null;
  readonly pageSize: number;
};
